import React, { PureComponent } from 'react'
import { Col, Row, Button, ProgressBar, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import ReactDayPickerInput from 'react-day-picker/DayPickerInput';
//import Multiselect from 'react-multi-select-component';

import Navigation from '../../components/navigation/Navigation';
import { ErrorMessage, Loading } from '../../components/visuals/Visuals';
import Badge from '../../components/Badge';
import Multiselect from '../../components/Multiselect';

import { Prognosis } from '../../lib/utilities';
import { months_long_se, weekdays_long_se, weekdays_short_se, yearGradeOptions_GR_str, yearGradeOptions_GY_str } from '../../lib/constants';
import DataAccess from '../../lib/dataAccess/DataAccess';

import { IHighSchoolAdmissionStats, IHighSchoolExaminationStats } from '../../lib/types/types';
import { totalmem } from 'os';
import Table from '../../components/Table';
import { ColumnDescription } from 'react-bootstrap-table-next';

interface Props {
    role: string
}

interface AggregatedAdmissionStats {
    index: number,
    schoolId: string,
    schoolName: string,
    studentCount: number,
    gender: "K" | "M" | "TOTAL",
    meritPoints: number,
    basicAdmission: number,
    universityProgramAdmission: number,
    coreSubjectsCompleted: number,
    allGoals: number,
    goodGrades: number
}

interface AggregatedExaminationStats {
    index: number,
    schoolId: string,
    schoolName: string,
    studentCount: number,
    gender: "K" | "M" | "TOTAL",

    examination: number,
    meritValue: number,
    coursePointsTotal: number
}

interface State {
    error: string | null,
    isLoading: boolean,
    filtersModified: boolean,
    //options
    schoolOptions: { value:number, label:string, disabled?:boolean }[],
    schoolClassOptions: { [school:number] : { value:number, label:string, schoolId:number }[] },
    yearGradeOptions_GR: { value:string, label:string }[],
    yearGradeOptions_GY: { value:string, label:string }[],
    schoolProgramOptions: { [school:number] : { value:string, label:string, schoolId:number }[] },
    genderOptions: { value:string, label:string }[],
    //filters
    endDate?: Date,
    schoolIds: { value:number, label:string }[],
    classIds: { value:number, label:string }[],
    //genders: { value:string, label:string }[],
    yearGrades: { value:string, label:string }[],
    programs: { value:string, label:string }[],
    gyPrograms: { [key: string]: boolean },
    schoolType?: "GR" | "GY",
    activeSchoolType?: "GR" | "GY",
    isDataFetched: boolean,

    admissionStats: IHighSchoolAdmissionStats[],
    aggregated_admissionStats: AggregatedAdmissionStats[],
    examinationStats: IHighSchoolExaminationStats[],
    aggregated_examinationStats: AggregatedExaminationStats[]
}

const color2Class = (i: number) => {
    if(i < 0.9) {
        return "bg-light-red";
    }
    else if(i < 0.97) {
        return "bg-yellow";
    }
    else if(i < 1) {
        return "bg-light-green";
    }
    else if (i >= 1) {
        return "bg-green";
    }
    
    return "";
}

const admissionColumns : ColumnDescription<AggregatedAdmissionStats, any>[] = [{
        dataField: 'index',
        text: 'ID',
        hidden: true
    }, {
        dataField: 'schoolName',
        //filter: textFilter(),
        text: 'Skola',
        formatter: (cell) => {
            return <div>{cell}</div>
        }
    }, {
        dataField: 'studentCount',
        //filter: textFilter(),
        text: 'Antal elever',
        sort: false
    }, {
        dataField: 'gender',
        //filter: textFilter(),
        text: 'Kön',
        formatter: (cell) => {
            return <div>{cell == 'K' ? 'Flicka' : cell == 'M' ? 'Pojke' : cell == 'TOTAL' ? 'Total' : cell}</div>
        },
        sort: false
    }, {
        dataField: 'meritPoints',
        //filter: textFilter(),
        text: 'Meritvärde',
        sort: false,
        formatter: (cell) => {
            return <div title={cell}>{Math.round(cell * 10) / 10}</div>
        }
    }, {
        dataField: 'basicAdmission',
        //filter: textFilter(),
        text: 'Grundläggande behörighet',
        headerFormatter: () => <div title={"Godkänt i kärnämnen och 5 andra ämnen"} style={{textAlign:'center'}}>Grundläggande<br/>gymnasiebehörighet</div>,
        formatter: (cell, r) => {
            return <div title={(cell * 100) + "%"} style={{textAlign:'center'}}>{Math.round(cell * 1000) / 10}%</div>
        },
        classes: (_, r) => { return r.schoolName != "Total" ? color2Class(r.basicAdmission) : ""; },
        sort: false
    }, {
        dataField: 'universityProgramAdmission',
        //filter: textFilter(),
        text: 'Högskoleförberedande',
        headerFormatter: () => <div title={"Godkänt i kärnämnen och 8 andra ämnen"} style={{textAlign:'center'}}>Högskoleförberedande<br/>gymnasiebehörighet</div>,
        formatter: (cell, r) => {
            return <div title={(cell * 100) + "%"} style={{textAlign:'center'}}>{Math.round(cell * 1000) / 10}%</div>
        },
        classes: (_, r) => { return r.schoolName != "Total" ? color2Class(r.universityProgramAdmission) : ""; },
        sort: false
    }, {
        dataField: 'coreSubjectsCompleted',
        //filter: textFilter(),
        text: 'Ma/SV/EN',
        headerFormatter: () => (<div title={"Godkänt i grundläggande svenska/SVA, matematik, engelska"}>Engelska<br/>Matematik<br/>Svenska/SVA</div>),
        formatter: (cell, r) => {
            return <div title={(cell * 100) + "%"} style={{textAlign:'center'}}>{Math.round(cell * 1000) / 10}%</div>
        },
        classes: (_, r) => { return r.schoolName != "Total" ? color2Class(r.coreSubjectsCompleted) : ""; },
        sort: false
    }, {
        dataField: 'allGoals',
        //filter: textFilter(),
        text: 'Alla mål',
        formatter: (cell, r) => {
            return <div title={(cell * 100) + "%"} style={{textAlign:'center'}}>{Math.round(cell * 1000) / 10}%</div>
        },
        classes: (_, r) => { return r.schoolName != "Total" ? color2Class(r.allGoals) : ""; },
        sort: false
    }, {
        dataField: 'goodGrades',
        //filter: textFilter(),
        text: 'A/B Betyg',
        formatter: (cell, r) => {
            return <div title={(cell * 100) + "%"} style={{textAlign:'center'}}>{Math.round(cell * 1000) / 10}%</div>
        },
        classes: (_, r) => { return r.schoolName != "Total" ? color2Class(r.goodGrades) : ""; },
        sort: false
    }
];

const examColumns : ColumnDescription<AggregatedExaminationStats, any>[] = [{
        dataField: 'index',
        text: 'ID',
        hidden: true
    }, {
        dataField: 'schoolName',
        //filter: textFilter(),
        text: 'Skola',
        formatter: (cell) => {
            return <div>{cell}</div>
        }
    }, {
        dataField: 'studentCount',
        //filter: textFilter(),
        text: 'Antal elever',
        sort: false
    }, {
        dataField: 'gender',
        //filter: textFilter(),
        text: 'Kön',
        formatter: (cell) => {
            return <div>{cell == 'K' ? 'Flicka' : cell == 'M' ? 'Pojke' : cell == 'TOTAL' ? 'Total' : cell}</div>
        },
        sort: false
    }, {
        dataField: 'examination',
        //filter: textFilter(),
        text: 'Grundläggande behörighet',
        headerFormatter: () => <div style={{textAlign:'center'}}>Examen</div>,
        formatter: (cell, r) => {
            return <div title={(cell * 100) + "%"} style={{textAlign:'center'}}>{Math.round(cell * 1000) / 10}%</div>
        },
        classes: (_, r) => { return r.schoolName != "Total" ? color2Class(r.examination) : ""; },
        sort: false
    }, {
        dataField: 'meritValue',
        //filter: textFilter(),
        text: 'Genomsnittligt GBP',
        headerFormatter: () => (<div>Genomsnittligt GBP</div>),
        formatter: (cell, r) => {
            return <div title={cell}>{Math.round(cell * 10) / 10}</div>
        },
        sort: false
    }, {
        dataField: 'coursePointsTotal',
        //filter: textFilter(),
        text: 'Genomsnittligt Betygspoäng',
        headerFormatter: () => <div>Genomsnittligt<br/>Betygspoäng</div>,
        formatter: (cell, r) => {
            return <div title={cell}>{Math.round(cell * 10) / 10}</div>
        },
        sort: false
    }
];

export default class AdmissionExamStatisticsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.getFilterOptions = this.getFilterOptions.bind(this);
        this.fetchData = this.fetchData.bind(this);
        this.changeSchool = this.changeSchool.bind(this);

        this.removeClassIdFilter = this.removeClassIdFilter.bind(this);
        //this.removeGenderFilter = this.removeGenderFilter.bind(this);
        this.removeYearGradeFilter = this.removeYearGradeFilter.bind(this);
        this.removeProgramFilter = this.removeProgramFilter.bind(this);

        this.onFilter = this.onFilter.bind(this);
        this.filterAdmissionStats = this.filterAdmissionStats.bind(this);
        this.filterExaminationStats = this.filterExaminationStats.bind(this);

        this.renderAdmission= this.renderAdmission.bind(this);
        this.renderExamination = this.renderExamination.bind(this);

        let _today = new Date();
        let today = new Date(`${_today.toDateString()}`);
        //let startDate = today.getMonth() > 6 ? new Date(`${today.getFullYear()}-08-01`) : new Date(`${today.getFullYear()-1}-08-01`)

        this.state = {
            error: null,
            isLoading: true,
            filtersModified: false,
            //options
            schoolOptions: [],
            schoolClassOptions: {},
            yearGradeOptions_GR: yearGradeOptions_GR_str,
            yearGradeOptions_GY: yearGradeOptions_GY_str,
            schoolProgramOptions: {},
            genderOptions: [{value:'K', label:'Flicka'}, {value:'M', label: 'Pojke'}],
            //filters
            endDate: today,
            schoolIds: [],
            classIds: [],
            //genders: [],
            yearGrades: [],
            programs: [],
            gyPrograms: {},

            schoolType: undefined,
            activeSchoolType: undefined,
            isDataFetched: false,

            admissionStats: [],
            aggregated_admissionStats: [],
            examinationStats: [],
            aggregated_examinationStats: []
        }
    }

    componentDidMount() {
        this.getFilterOptions();
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if(this.props.role != prevProps.role) {
            this.getFilterOptions();
        }
    }

    async getFilterOptions() {
        this.setState({ isLoading: true, error: null });

        let role = this.props.role;

        try {
            let classesPromise = DataAccess.General.getAllClasses();
            //Get programs
            let schoolPromise = role == "USER" ? DataAccess.General.getSchools() : DataAccess.General.getAllSchools();
            let programPromise = DataAccess.General.getAllPrograms();
    
            let classes = await classesPromise;
            let schools = await schoolPromise;
            let programs = await programPromise;
            
            const programObject = [{id: -1, code: 'GR', name: 'Grundskola'}].concat(programs).reduce((acc: any, program) => {
                acc[program.code] = program;
                return acc;
            }, {});
            const uniquePrograms = Object.keys(programObject).map(key => programObject[key]).filter(p => p.name != "undefined");

            const gyPrograms = programs.reduce((acc: any, p) => {
                    acc[p.schoolUnitId] = true;
                    return acc;
                }, {});

            let classOptions = classes.filter(c => c.school != null)
                                .map(c => ({value: c.id, label: `${c.name}, ${c.school?.name}`, schoolId: (c.school?.id || -1)}));
            let schoolClassOptions = classOptions.reduce(
                (acc: { [id: string]: { value: number, label: string, schoolId: number}[]}, 
                 c: { value: number, label: string, schoolId: number}) => {
                    return {...acc, [c.schoolId]: [...(acc[c.schoolId] || []), c]};
                }, {});

            let schoolOptions = schools.map(s => ({value: s.id, label: s.name}));
            let programOptions = uniquePrograms.map(p => ({value: p.code, label: p.name, schoolId: p.schoolUnitId}));
            let schoolProgramOptions = programOptions.reduce(
                (acc: { [id: string]: { value: number, label: string, schoolId: number}[]}, 
                 c: { value: number, label: string, schoolId: number}) => {
                    return {...acc, [c.schoolId]: [...(acc[c.schoolId] || []), c]};
                }, {});

            this.setState({ error: null, isLoading: false, schoolClassOptions, schoolOptions, schoolProgramOptions, gyPrograms });
        }
        catch(e) {
            this.setState({ error: e, isLoading: false });
        }
    }

    changeSchool(schools: { value: number, label: string, disabled?: boolean}[]) {

        const { gyPrograms, schoolOptions } = this.state

        if(schools.length == 0) {
            this.setState({schoolIds: [], schoolType: undefined, schoolOptions: schoolOptions.map(s => ({...s, disabled: false}))});
            return;
        }

        const isGY = gyPrograms[schools[0].value];
        const newSchoolType = isGY ? "GY" : "GR";
        const schoolTypeChanged = newSchoolType != this.state.schoolType;

        const modifiedSchoolOptions = schoolOptions.map(s => {
                const isSchoolGY = gyPrograms[s.value] != undefined
                return ({...s, disabled: !(isGY && isSchoolGY) && !(!isGY && !isSchoolGY)})}
            );

        this.setState({
            schoolIds: schools, 
            schoolOptions: modifiedSchoolOptions,
            schoolType: newSchoolType, 
            classIds: [], 
            yearGrades: schoolTypeChanged ? [] : this.state.yearGrades,
            programs: []
        });
    }

    removeClassIdFilter(index: number) {
        this.setState(({classIds}) => ({ classIds: [...classIds.slice(0, index), ...classIds.slice(index + 1)], filtersModified: true }));
    }

    /*removeGenderFilter(index: number) {
        this.setState(({genders}) => ({ genders: [...genders.slice(0, index), ...genders.slice(index + 1)], filtersModified: true }));
    }*/
    
    removeYearGradeFilter(index: number) {
        this.setState(({yearGrades}) => ({ yearGrades: [...yearGrades.slice(0, index), ...yearGrades.slice(index + 1)], filtersModified: true }));
    }
    
    removeProgramFilter(index: number) {
        this.setState(({programs}) => ({ programs: [...programs.slice(0, index), ...programs.slice(index + 1)], filtersModified: true }));
    }

    async fetchData() {
        this.setState({isLoading: true, filtersModified: false}, async () => {
            try {
                const { endDate, schoolIds, schoolType } = this.state;
    
                if(endDate == undefined) {
                    throw('Datumintervall saknas...');
                }
    
                if(schoolIds.length == 0) {
                    throw('Val av skola saknas...');
                }
    
                if(schoolType == "GY") {
                    let db_data = await DataAccess.Statistics.GetHighSchoolExaminationStats(endDate, schoolIds.map(s => s.value));
    
                    let aggregated_data = this.filterExaminationStats(db_data);
        
                    this.setState({examinationStats: db_data, aggregated_examinationStats: aggregated_data, error: null, isLoading: false, isDataFetched: true, activeSchoolType: schoolType});
                }
                else if(schoolType == "GR") {
                    let db_data = await DataAccess.Statistics.GetHighSchoolAdmissionStats(endDate, schoolIds.map(s => s.value));
    
                    let aggregated_data = this.filterAdmissionStats(db_data);
        
                    this.setState({admissionStats: db_data, aggregated_admissionStats: aggregated_data, error: null, isLoading: false, isDataFetched: true, activeSchoolType: schoolType});
                }
                else {
                    this.setState({error: null, isLoading: false})
                }
            }
            catch (error) {
                console.log({error})
                this.setState({error, isLoading: false, filtersModified: true});
            }
        });
    }

    onFilter() {
        this.setState({isLoading: true, filtersModified: false}, () => {
            if(this.state.schoolType == "GR") {
                const aggregated_data = this.filterAdmissionStats(this.state.admissionStats);
                this.setState({error: null, isLoading: false, aggregated_admissionStats: aggregated_data});
            }
            else if(this.state.schoolType == "GY") {
                const aggregated_data = this.filterExaminationStats(this.state.examinationStats);
                this.setState({error: null, isLoading: false, aggregated_examinationStats: aggregated_data});
            }
            else {
                this.setState({error: "Saknar skoltyp...", isLoading: false, filtersModified: true})
            }
        });
    }

    filterAdmissionStats(data: IHighSchoolAdmissionStats[]): AggregatedAdmissionStats[] {
        const { classIds, yearGrades, schoolIds } = this.state;
        const classFilter: { [id:number]: boolean } = classIds.reduce((acc, c) => ({ ...acc, [c.value]: true}), {});
        const yearFilter: { [id:string]: boolean } = yearGrades.reduce((acc, y) => ({ ...acc, [y.value]: true}), {});
        //const genderFilter: { [id:string]: boolean } = genders.reduce((acc, g) => ({ ...acc, [g.value]: true}), {});

        var filteredData = data.filter(r => (classIds.length == 0 || classFilter[r.classId || -1] != undefined)
                                            && (yearGrades.length == 0 || yearFilter[r.year] != undefined));

        let reduceData = (index: number, schooId: string, schoolName: string, gender: "K" | "M" | "TOTAL", arr: IHighSchoolAdmissionStats[]) => {
            let summary = arr.reduce((acc: AggregatedAdmissionStats, e: IHighSchoolAdmissionStats) => {
                return {
                    ...acc,
                    studentCount: acc.studentCount + 1,
                    meritPoints: acc.meritPoints + e.meritPoints,
                    basicAdmission: acc.basicAdmission + (e.basicAdmissionApproved ? 1 : 0),
                    universityProgramAdmission: acc.universityProgramAdmission + (e.universityProgramApproved ? 1 : 0),
                    coreSubjectsCompleted: acc.coreSubjectsCompleted + (e.coreSubjectsCompleted ? 1 : 0),
                    allGoals: acc.allGoals + (e.allGoals ? 1 : 0),
                    goodGrades: acc.goodGrades + e.goodGrades
                };
            }, {
                index: index,
                schoolId: schooId,
                schoolName: schoolName,
                studentCount: 0,
                gender: gender,
                meritPoints: 0,
                basicAdmission: 0,
                universityProgramAdmission: 0,
                coreSubjectsCompleted: 0,
                allGoals: 0,
                goodGrades: 0
             });

             const sC = summary.studentCount;

             let result = {
                ...summary,
                meritPoints: sC > 0 ? summary.meritPoints / sC : 0,
                basicAdmission: sC > 0 ? summary.basicAdmission / sC : 0,
                universityProgramAdmission: sC > 0 ? summary.universityProgramAdmission / sC : 0,
                coreSubjectsCompleted: sC > 0 ? summary.coreSubjectsCompleted / sC : 0,
                allGoals: sC > 0 ? summary.allGoals / sC : 0,
                goodGrades: sC > 0 ? summary.goodGrades / sC : 0
             }

             return result;
        }

        let schoolGroups = schoolIds.reduce((acc, s) => ({...acc, [s.value]: { name: s.label, K: [], M: [] }}), {});
        let grouped = filteredData.reduce((acc: { [id:number]: { name:string, K: IHighSchoolAdmissionStats[], M: IHighSchoolAdmissionStats[]} }, r) => {
            //@ts-ignore
            let schoolObj: any = acc[r.schoolId];
            return {
                ...acc,
                [r.schoolId]: {
                    ...schoolObj,
                    [r.gender]: [ ...schoolObj[r.gender], r]
                }
            };
        }, schoolGroups);

        let calc_data = Object.keys(grouped).map((key, index) => {
            //@ts-ignore
            let group = grouped[key];

            let f = reduceData((index * 10), key, group.name, "K", group.K);
            let m = reduceData((index * 10 + 1), key, group.name, "M", group.M);
            let totalStudents = f.studentCount + m.studentCount;
            let total: AggregatedAdmissionStats = {
                index: index * 10 + 2,
                schoolId: key,
                schoolName: group.name,
                studentCount: totalStudents,
                gender: "TOTAL",
                meritPoints: totalStudents > 0 ? ((f.meritPoints * f.studentCount) + (m.meritPoints * m.studentCount)) / totalStudents : 0,
                basicAdmission: totalStudents > 0 ? ((f.basicAdmission * f.studentCount) + (m.basicAdmission * m.studentCount)) / totalStudents : 0,
                universityProgramAdmission: totalStudents > 0 ? ((f.universityProgramAdmission * f.studentCount) + (m.universityProgramAdmission * m.studentCount)) / totalStudents : 0,
                coreSubjectsCompleted: totalStudents > 0 ? ((f.coreSubjectsCompleted * f.studentCount) + (m.coreSubjectsCompleted * m.studentCount)) / totalStudents : 0,
                allGoals: totalStudents > 0 ? ((f.allGoals * f.studentCount) + (m.allGoals * m.studentCount)) / totalStudents : 0,
                goodGrades: totalStudents > 0 ? ((f.goodGrades * f.studentCount) + (m.goodGrades * m.studentCount)) / totalStudents : 0
            };

            return [ f, m, total ];
        }).flat();

        return calc_data;
    }

    filterExaminationStats(data: IHighSchoolExaminationStats[]): AggregatedExaminationStats[] {
        const { classIds, programs, yearGrades, schoolIds } = this.state;

        const classFilter: { [id:number]: boolean } = classIds.reduce((acc, c) => ({ ...acc, [c.value]: true}), {});
        const programFilter: { [id:string]: boolean } = programs.reduce((acc, c) => ({ ...acc, [c.value]: true}), {});
        const yearFilter: { [id:string]: boolean } = yearGrades.reduce((acc, y) => ({ ...acc, [y.value]: true}), {});
        //const genderFilter: { [id:string]: boolean } = genders.reduce((acc, g) => ({ ...acc, [g.value]: true}), {});
        
        var filteredData = data.filter(r => (classIds.length == 0 || classFilter[r.classId || -1] != undefined)
                                            && (programs.length == 0 || programFilter[r.program] != undefined)
                                            && (yearGrades.length == 0 || yearFilter[r.year] != undefined));

        let reduceData = (index: number, schooId: string, schoolName: string, gender: "K" | "M" | "TOTAL", arr: IHighSchoolExaminationStats[]) => {
            let summary = arr.reduce((acc: AggregatedExaminationStats, e: IHighSchoolExaminationStats) => {
                return {
                    ...acc,
                    studentCount: acc.studentCount + 1,
                    examination: acc.examination + (e.examination ? 1 : 0),
                    meritValue: Number.isNaN(acc.meritValue) ? acc.meritValue : acc.meritValue + e.meritValue,
                    coursePointsTotal: acc.coursePointsTotal + e.coursePoints
                };
            }, {
                index: index,
                schoolId: schooId,
                schoolName: schoolName,
                studentCount: 0,
                gender: gender,

                examination: 0,
                meritValue: 0,
                coursePointsTotal: 0
            });

            return summary;
        }

        let averageData = (d: AggregatedExaminationStats) => ({
            ...d,
            examination: d.studentCount > 0 ? d.examination / d.studentCount : 0,
            meritValue: d.studentCount > 0 ? d.meritValue / d.studentCount : 0,
            coursePointsTotal: d.studentCount > 0 ? d.coursePointsTotal / d.studentCount : 0
        })
        
        let schoolGroups = schoolIds.reduce((acc, s) => ({...acc, [s.value]: { name: s.label, K: [], M: [] }}), {});
        let grouped = filteredData.reduce((acc: { [id:number]: { name:string, K: IHighSchoolExaminationStats[], M: IHighSchoolExaminationStats[]} }, r) => {
            //@ts-ignore
            let schoolObj: any = acc[r.schoolId];
            return {
                ...acc,
                [r.schoolId]: {
                    ...schoolObj,
                    [r.gender]: [ ...schoolObj[r.gender], r]
                }
            };
        }, schoolGroups);

        let calc_data = Object.keys(grouped).map((key, index) => {
            //@ts-ignore
            let group = grouped[key];

            let f = reduceData((index * 10), key, group.name, "K", group.K);
            let m = reduceData((index * 10 + 1), key, group.name, "M", group.M);
            let t: AggregatedExaminationStats = {
                index: (index * 10 + 1),
                schoolId: key,
                schoolName: group.name,
                studentCount: f.studentCount + m.studentCount,
                gender: "TOTAL",

                examination: f.examination + m.examination,
                meritValue: f.meritValue + m.meritValue,
                coursePointsTotal: f.coursePointsTotal + m.coursePointsTotal
            };

            let female = averageData(f);
            let male = averageData(m);
            let total = averageData(t);

            return [ female, male, total ];
        }).flat();

        return calc_data;
    }

    renderAdmission(){
        const { aggregated_admissionStats, isLoading } = this.state;

        const femaleSum: AggregatedAdmissionStats = aggregated_admissionStats.filter(d => d.gender == "K")
                            .reduce((acc, e) => {
                                return {
                                    ...acc,
                                    studentCount: acc.studentCount + e.studentCount,
                                    meritPoints: acc.meritPoints + (e.meritPoints * e.studentCount),
                                    basicAdmission: acc.basicAdmission + (e.basicAdmission * e.studentCount),
                                    universityProgramAdmission: acc.universityProgramAdmission + (e.universityProgramAdmission * e.studentCount),
                                    coreSubjectsCompleted: acc.coreSubjectsCompleted + (e.coreSubjectsCompleted * e.studentCount),
                                    allGoals: acc.allGoals + (e.allGoals * e.studentCount),
                                    goodGrades: acc.goodGrades + (e.goodGrades * e.studentCount)
                                };
                            }, {
                                index: aggregated_admissionStats.length * 10,
                                schoolId: "-1",
                                schoolName: "Total",
                                studentCount: 0,
                                gender: "K",
                                meritPoints: 0,
                                basicAdmission: 0,
                                universityProgramAdmission: 0,
                                coreSubjectsCompleted: 0,
                                allGoals: 0,
                                goodGrades: 0
                            });
        const maleSum: AggregatedAdmissionStats = aggregated_admissionStats.filter(d => d.gender == "M")
                            .reduce((acc, e) => {
                                return {
                                    ...acc,
                                    studentCount: acc.studentCount + e.studentCount,
                                    meritPoints: acc.meritPoints + (e.meritPoints * e.studentCount),
                                    basicAdmission: acc.basicAdmission + (e.basicAdmission * e.studentCount),
                                    universityProgramAdmission: acc.universityProgramAdmission + (e.universityProgramAdmission * e.studentCount),
                                    coreSubjectsCompleted: acc.coreSubjectsCompleted + (e.coreSubjectsCompleted * e.studentCount),
                                    allGoals: acc.allGoals + (e.allGoals * e.studentCount),
                                    goodGrades: acc.goodGrades + (e.goodGrades * e.studentCount)
                                };
                            }, {
                                index: aggregated_admissionStats.length * 10 + 1,
                                schoolId: "-1",
                                schoolName: "Total",
                                studentCount: 0,
                                gender: "M",
                                meritPoints: 0,
                                basicAdmission: 0,
                                universityProgramAdmission: 0,
                                coreSubjectsCompleted: 0,
                                allGoals: 0,
                                goodGrades: 0
                            });

        const totalSum: AggregatedAdmissionStats = {
            index: aggregated_admissionStats.length * 10 + 2,
            schoolId: "-1",
            schoolName: "Total",
            studentCount: femaleSum.studentCount + maleSum.studentCount,
            gender: "TOTAL",
            meritPoints: femaleSum.meritPoints + maleSum.meritPoints,
            basicAdmission: femaleSum.basicAdmission + maleSum.basicAdmission,
            universityProgramAdmission: femaleSum.universityProgramAdmission + maleSum.universityProgramAdmission,
            coreSubjectsCompleted: femaleSum.coreSubjectsCompleted + maleSum.coreSubjectsCompleted,
            allGoals: femaleSum.allGoals + maleSum.allGoals,
            goodGrades: femaleSum.goodGrades + maleSum.goodGrades
        };

        const summarizeUndivided = (d: AggregatedAdmissionStats) => ({
            ...d,
            meritPoints: d.studentCount > 0 ? d.meritPoints / d.studentCount : 0,
            basicAdmission: d.studentCount > 0 ? d.basicAdmission / d.studentCount : 0,
            universityProgramAdmission: d.studentCount > 0 ? d.universityProgramAdmission / d.studentCount : 0,
            coreSubjectsCompleted: d.studentCount > 0 ? d.coreSubjectsCompleted / d.studentCount : 0,
            allGoals: d.studentCount > 0 ? d.allGoals / d.studentCount : 0,
            goodGrades: d.studentCount > 0 ? d.goodGrades / d.studentCount : 0
        })
        const femaleRow = summarizeUndivided(femaleSum)
        const maleRow = summarizeUndivided(maleSum)
        const totalRow = summarizeUndivided(totalSum)

        return <div style={{marginTop: '1rem'}}>
            <Table
                keyField={'courseId'}
                columns={admissionColumns}
                data={[...aggregated_admissionStats, femaleRow, maleRow, totalRow] || [femaleRow, maleRow, totalRow]}
                loading={isLoading}
                searchHidden={true}
                bsTableProps={{
                    rowStyle: (row: AggregatedAdmissionStats, rowIndex: number) => {
                        if(row.schoolName == "Total" && row.gender == "TOTAL") {
                            return {backgroundColor: '#b2b2b2'}
                        }
                        else if(row.schoolName == "Total") {
                            return {backgroundColor: '#d2d2d2'}
                        }
                        else { return { } }
                    }
                }}
            />
        </div>
    }

    renderExamination() {
        const { aggregated_examinationStats, isLoading } = this.state;

        const femaleSum: AggregatedExaminationStats = aggregated_examinationStats.filter(d => d.gender == "K")
                            .reduce((acc, e) => {
                                return {
                                    ...acc,
                                    studentCount: acc.studentCount + e.studentCount,
                                    examination: acc.examination + (e.examination * e.studentCount),
                                    meritValue: acc.meritValue + (e.meritValue * e.studentCount),
                                    coursePointsTotal: acc.coursePointsTotal + (e.coursePointsTotal * e.studentCount)
                                };
                            }, {
                                index: aggregated_examinationStats.length * 10,
                                schoolId: "-1",
                                schoolName: "Total",
                                studentCount: 0,
                                gender: "K",
                                examination: 0,
                                meritValue: 0,
                                coursePointsTotal: 0
                            });
        const maleSum: AggregatedExaminationStats = aggregated_examinationStats.filter(d => d.gender == "M")
                            .reduce((acc, e) => {
                                return {
                                    ...acc,
                                    studentCount: acc.studentCount + e.studentCount,
                                    examination: acc.examination + (e.examination * e.studentCount),
                                    meritValue: acc.meritValue + (e.meritValue * e.studentCount),
                                    coursePointsTotal: acc.coursePointsTotal + (e.coursePointsTotal * e.studentCount)
                                };
                            }, {
                                index: aggregated_examinationStats.length * 10 + 1,
                                schoolId: "-1",
                                schoolName: "Total",
                                studentCount: 0,
                                gender: "M",
                                examination: 0,
                                meritValue: 0,
                                coursePointsTotal: 0
                            });

        const totalSum: AggregatedExaminationStats = {
            index: aggregated_examinationStats.length * 10 + 2,
            schoolId: "-1",
            schoolName: "Total",
            studentCount: femaleSum.studentCount + maleSum.studentCount,
            gender: "TOTAL",
            examination: femaleSum.examination + maleSum.examination,
            meritValue: femaleSum.meritValue + maleSum.meritValue,
            coursePointsTotal: femaleSum.coursePointsTotal + maleSum.coursePointsTotal,
        };

        const summarizeUndivided = (d: AggregatedExaminationStats) => ({
            ...d,
            examination: d.studentCount > 0 ? d.examination / d.studentCount : 0,
            meritValue: d.studentCount > 0 ? d.meritValue / d.studentCount : 0,
            coursePointsTotal: d.studentCount > 0 ? d.coursePointsTotal / d.studentCount : 0,
        })
        const femaleRow = summarizeUndivided(femaleSum)
        const maleRow = summarizeUndivided(maleSum)
        const totalRow = summarizeUndivided(totalSum)

        return <div style={{marginTop: '1rem'}}>
            <Table
                keyField={'courseId'}
                columns={examColumns}
                data={[...aggregated_examinationStats, femaleRow, maleRow, totalRow] || [femaleRow, maleRow, totalRow]}
                loading={isLoading}
                searchHidden={true}
                bsTableProps={{
                    rowStyle: (row: AggregatedAdmissionStats, rowIndex: number) => {
                        if(row.schoolName == "Total" && row.gender == "TOTAL") {
                            return {backgroundColor: '#b2b2b2'}
                        }
                        else if(row.schoolName == "Total") {
                            return {backgroundColor: '#d2d2d2'}
                        }
                        else { return { } }
                    }
                }}
            />
        </div>
    }
    
    render() {
        const { 
            error, isLoading, endDate, schoolIds, schoolType, activeSchoolType,
            filtersModified, 
            classIds, schoolClassOptions,
            schoolOptions,
            //genders, genderOptions,
            yearGrades,
            programs, schoolProgramOptions,
        } = this.state;

        const classOptions = schoolIds.map(s => schoolClassOptions[s.value]).flat();
        const programOptions = schoolIds.map(s => schoolProgramOptions[s.value]).flat();

        const isGY = activeSchoolType == "GY";

        return (
            <div className="render-home">
                <Navigation role={this.props.role}/>
                <div className="junit-container">
                    <ErrorMessage hidden={error === null} style={{marginBottom:'1rem'}}>{"Fel"}</ErrorMessage>
                    <div style={{margin: '1rem 0'}}>
                        <Row style={{width: '100%'}}>
                            <Col xs={12} sm={6} style={{paddingTop:'1rem'}}>
                                <table style={{margin: '0 1rem', width: '100%'}}>
                                    <tr>
                                        <td style={{width:'4rem'}}>
                                            <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Skola:</span>
                                        </td>
                                        <td style={{minWidth:'100px'}}>
                                            <Multiselect
                                                value={schoolIds}
                                                onChange={(selected: any) => this.changeSchool(selected)}
                                                options={schoolOptions}
                                                maxResult={1000}
                                            />
                                        </td>
                                    </tr>
                                </table>
                            </Col>
                            <Col xs={12} sm={4} style={{paddingTop:'1rem'}}>
                                
                            <table style={{margin: '0 1rem', width: '100%'}}>
                                    <tr>
                                        <td style={{width:'4rem', textAlign: 'center'}}>
                                            <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Brytdatum:</span>
                                        </td>
                                        <td style={{minWidth:'100px'}}>
                                            <ReactDayPickerInput 
                                                formatDate={(date) => date.toISOString().slice(0, 10)}
                                                value={endDate}
                                                placeholder={'ÅÅÅÅ-MM-DD'}
                                                style={{lineHeight:'2.25rem', width: '100%'}}
                                                inputProps={{ style:{ textAlign:'center', borderRadius: '0.25rem', border:'solid 1px #aaa', width: '100%' } }}
                                                dayPickerProps={{
                                                    locale: 'sv',
                                                    months: months_long_se,
                                                    weekdaysLong: weekdays_long_se,
                                                    weekdaysShort: weekdays_short_se
                                                }}
                                                onDayChange={(e) => this.setState({endDate: e, filtersModified: true})}
                                            />
                                        </td>
                                    </tr>
                                </table>
                            </Col>
                            <Col xs={12} sm={2} style={{paddingTop:'1rem',textAlign: 'end'}}>
                                <Button disabled={!(endDate && schoolIds.length > 0) || isLoading} onClick={this.fetchData}>Hämta data</Button>
                            </Col>
                        </Row>
                    </div>
                    <div style={{padding: '1rem', paddingRight: 0, borderTop: 'solid 1px lightgrey'}}>
                        <Row style={{width: '100%'}}>
                            <Col xs={12} sm={isGY ? 4 : 6} style={{paddingTop:'1rem'}}>
                                <Multiselect
                                    value={classIds}
                                    disabled={schoolIds.length == 0}
                                    onChange={(selected: any) => this.setState({classIds: selected, filtersModified: true})}
                                    options={/*schoolIdArr.length > 0 ? classOptions.filter(e => schoolIdArr.indexOf(e.schoolId) != -1) :*/ classOptions}
                                    //filterOptions={this.selectFilter}
                                    valueRenderer={() => "Filtrera klasser"}
                                />
                            </Col>
                            {isGY ? <Col xs={12} sm={3} style={{paddingTop:'1rem'}}>
                                <Multiselect
                                    value={programs}
                                    disabled={schoolIds.length == 0}
                                    onChange={(selected: any) => this.setState({programs: selected, filtersModified: true})}
                                    options={programOptions}
                                    valueRenderer={() => "Utbildningsväg"}
                                />
                            </Col> : null }
                            <Col xs={12} sm={isGY ? 3 : 4} style={{paddingTop:'1rem'}}>
                                <Multiselect
                                    value={yearGrades}
                                    disabled={schoolIds.length == 0}
                                    onChange={(selected: any) => this.setState({yearGrades: selected, filtersModified: true})}
                                    options={schoolType == "GR" ? yearGradeOptions_GR_str : schoolType == "GY" ? yearGradeOptions_GY_str : []}
                                    //filterOptions={this.selectFilter}
                                    disableSearch
                                    valueRenderer={() => "Årskurs"}
                                />
                            </Col>
                            {/*<Col xs={12} sm={2} style={{paddingTop:'1rem'}}>
                                <Multiselect
                                    value={genders}
                                    onChange={(selected: any) => this.setState({genders: selected, filtersModified: true})}
                                    options={genderOptions}
                                    //filterOptions={this.selectFilter}
                                    disableSearch
                                    valueRenderer={() => "Kön"}
                                />
                            </Col>*/}
                            <Col xs={12} sm={2} style={{paddingTop:'1rem', textAlign: 'end'}}>
                                <Button disabled={!filtersModified || isLoading} onClick={this.onFilter}>
                                    Applicera filter
                                </Button>
                            </Col>
                        </Row>
                    </div>
                    <div style={{padding: '0.5rem', borderTop: 'solid 1px lightgrey'}}>
                        { classIds.length == 0 && yearGrades.length == 0 && programs.length == 0 ? 
                            <div style={{color:'grey', padding: '1rem'}}>Inga filter tillagda</div> 
                        : null}
                        {classIds.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#6176BD', color:'white'}} 
                                onHide={() => this.removeClassIdFilter(index)}
                            />
                        )}
                        {programs.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#94A187', color:'white'}} 
                                onHide={() => this.removeProgramFilter(index)}
                            />
                        )}
                        {yearGrades.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#DF9216', color:'white'}} 
                                onHide={() => this.removeYearGradeFilter(index)}
                            />
                        )}
                        {/*genders.map((e, index) => 
                            <Badge 
                                label={e.label} 
                                style={{backgroundColor: '#DB436B', color:'white'}} 
                                onHide={() => this.removeGenderFilter(index)}
                            />
                        )*/}
                    </div>
                    <div style={{borderTop: 'solid 1px lightgrey', overflow: 'hidden'}}>
                        {isLoading ? 
                            <div style={{
                                position: 'absolute',
                                width: '100vw',
                                display: 'flex',
                                alignItems: 'center',
                                verticalAlign: 'middle',
                                left: 0,
                                height: '600px',
                                textAlign: 'center',
                                backgroundColor: '#ffffffd2',
                                zIndex: 10
                            }}>
                                <div style={{textAlign: 'center', width: '100%'}}>
                                    <Loading>Laddar...</Loading>
                                </div>
                            </div>
                            : null
                        }
                        <div style={{minHeight: '600px'}}>
                            { activeSchoolType == 'GR' ?
                                this.renderAdmission()
                              : activeSchoolType == 'GY' ?
                                this.renderExamination()
                              : null
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}